import { useState, useEffect } from 'react';
import './ProjectForm.css';
import FileUpload from './FileUpload';

const API_URL = import.meta.env.VITE_API_URL || '';

const jsonHeaders = (token) => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${token}`,
});

/** Create → POST /api/projects. Edit → POST /api/update-project (avoids /api/projects/[id] 405 on Vercel). */
async function saveProjectRequest(projectId, isEdit, token, formBody) {
  if (!isEdit) {
    return fetch(`${API_URL}/api/projects`, {
      method: 'POST',
      headers: jsonHeaders(token),
      body: JSON.stringify(formBody),
    });
  }
  return fetch(`${API_URL}/api/update-project`, {
    method: 'POST',
    headers: jsonHeaders(token),
    body: JSON.stringify({ ...formBody, id: String(projectId) }),
  });
}

const ProjectForm = ({ project, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    title: '',
    category: 'BRANDING',
    media: '',
    mediaType: 'image',
    thumbnail: '',
    backgroundColor: '#000000',
    projectContent: {
      sections: []
    }
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (project) {
      setFormData({
        title: project.title || '',
        category: project.category || 'BRANDING',
        media: project.media || '',
        mediaType: project.mediaType || 'image',
        thumbnail: project.thumbnail || '',
        backgroundColor: project.backgroundColor || '#000000',
        projectContent: project.projectContent || { sections: [] }
      });
    }
  }, [project]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddSection = () => {
    setFormData(prev => ({
      ...prev,
      projectContent: {
        sections: [
          ...prev.projectContent.sections,
          {
            type: 'media',
            media: '',
            alt: ''
          }
        ]
      }
    }));
  };

  const handleSectionChange = (index, field, value) => {
    const newSections = [...formData.projectContent.sections];
    newSections[index] = {
      ...newSections[index],
      [field]: value
    };
    setFormData(prev => ({
      ...prev,
      projectContent: { sections: newSections }
    }));
  };

  const handleRemoveSection = (index) => {
    const newSections = formData.projectContent.sections.filter((_, i) => i !== index);
    setFormData(prev => ({
      ...prev,
      projectContent: { sections: newSections }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const token = localStorage.getItem('adminToken');

    try {
      const response = await saveProjectRequest(
        project?._id,
        Boolean(project),
        token,
        formData
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to save project');
      }

      const saved = data.data ?? null;
      onSubmit(saved);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="project-form-container">
      <div className="project-form-card">
        <div className="form-header">
          <h2>{project ? 'Edit Project' : 'Add New Project'}</h2>
          <button onClick={onCancel} className="close-btn">×</button>
        </div>

        {error && (
          <div className="form-error">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="project-form">
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="title">Project Title *</label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="Enter project title"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="category">Category *</label>
              <select
                id="category"
                name="category"
                value={formData.category}
                onChange={handleChange}
                required
              >
                <option value="BRANDING">BRANDING</option>
                <option value="Marketing">Marketing</option>
                <option value="Advertising">Advertising</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="media">Main Media URL *</label>
              <input
                type="text"
                id="media"
                name="media"
                value={formData.media}
                onChange={handleChange}
                placeholder="https://... or /project/..."
                required
              />
              <FileUpload
                onUploadComplete={(url) => {
                  setFormData(prev => ({ ...prev, media: url }));
                }}
                accept="image/*,video/*"
                label="Upload Main Media"
              />
            </div>

            <div className="form-group">
              <label htmlFor="mediaType">Media Type *</label>
              <select
                id="mediaType"
                name="mediaType"
                value={formData.mediaType}
                onChange={handleChange}
                required
              >
                <option value="image">Image</option>
                <option value="video">Video</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="thumbnail">Thumbnail URL</label>
              <input
                type="text"
                id="thumbnail"
                name="thumbnail"
                value={formData.thumbnail}
                onChange={handleChange}
                placeholder="https://... or /project/..."
              />
              <FileUpload
                onUploadComplete={(url) => {
                  setFormData(prev => ({ ...prev, thumbnail: url }));
                }}
                accept="image/*"
                label="Upload Thumbnail"
              />
            </div>

            <div className="form-group">
              <label htmlFor="backgroundColor">Background Color</label>
              <input
                type="color"
                id="backgroundColor"
                name="backgroundColor"
                value={formData.backgroundColor}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="sections-container">
            <div className="sections-header">
              <h3>Project Sections</h3>
              <button
                type="button"
                onClick={handleAddSection}
                className="add-section-btn"
              >
                + Add Section
              </button>
            </div>

            {formData.projectContent.sections.map((section, index) => (
              <div key={index} className="section-card">
                <div className="section-header">
                  <span>Section {index + 1}</span>
                  <button
                    type="button"
                    onClick={() => handleRemoveSection(index)}
                    className="remove-section-btn"
                  >
                    Remove
                  </button>
                </div>

                <div className="form-group">
                  <label>Section Type</label>
                  <select
                    value={section.type}
                    onChange={(e) => handleSectionChange(index, 'type', e.target.value)}
                  >
                    <option value="media">Media</option>
                    <option value="text">Text</option>
                    <option value="Video">Video</option>
                    <option value="text-section">Text Section</option>
                  </select>
                </div>

                {(section.type === 'media' || section.type === 'Video') && (
                  <>
                    <div className="form-group">
                      <label>Media URL (Desktop)</label>
                      <input
                        type="text"
                        value={section.media || section.imageName || ''}
                        onChange={(e) => handleSectionChange(index, 'media', e.target.value)}
                        placeholder="https://... or /project/..."
                      />
                      <FileUpload
                        onUploadComplete={(url) => handleSectionChange(index, 'media', url)}
                        accept="image/*,video/*"
                        label="Upload Desktop Media"
                      />
                    </div>
                    <div className="form-group">
                      <label>Media URL (Mobile) - Optional</label>
                      <input
                        type="text"
                        value={section.mobileMedia || ''}
                        onChange={(e) => handleSectionChange(index, 'mobileMedia', e.target.value)}
                        placeholder="https://... or /project/..."
                      />
                      <FileUpload
                        onUploadComplete={(url) => handleSectionChange(index, 'mobileMedia', url)}
                        accept="image/*,video/*"
                        label="Upload Mobile Media"
                      />
                    </div>
                    <div className="form-group">
                      <label>Alt Text</label>
                      <input
                        type="text"
                        value={section.alt || ''}
                        onChange={(e) => handleSectionChange(index, 'alt', e.target.value)}
                        placeholder="Alt text for media"
                      />
                    </div>
                  </>
                )}

                {(section.type === 'text' || section.type === 'text-section') && (
                  <>
                    <div className="form-group">
                      <label>Background Color</label>
                      <input
                        type="color"
                        value={section.backgroundColor || '#000000'}
                        onChange={(e) => handleSectionChange(index, 'backgroundColor', e.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label>Slogan</label>
                      <input
                        type="text"
                        value={section.slogan || ''}
                        onChange={(e) => handleSectionChange(index, 'slogan', e.target.value)}
                        placeholder="Project slogan"
                      />
                    </div>
                    <div className="form-group">
                      <label>Field Name</label>
                      <input
                        type="text"
                        value={section.fieldName || ''}
                        onChange={(e) => handleSectionChange(index, 'fieldName', e.target.value)}
                        placeholder="Industry field"
                      />
                    </div>
                    <div className="form-group">
                      <label>Services</label>
                      <input
                        type="text"
                        value={section.services || ''}
                        onChange={(e) => handleSectionChange(index, 'services', e.target.value)}
                        placeholder="Services provided"
                      />
                    </div>
                    <div className="form-group">
                      <label>Text Content</label>
                      <textarea
                        value={section.text || ''}
                        onChange={(e) => handleSectionChange(index, 'text', e.target.value)}
                        placeholder="Project description"
                        rows="4"
                      />
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>

          <div className="form-actions">
            <button type="button" onClick={onCancel} className="cancel-btn">
              Cancel
            </button>
            <button type="submit" className="submit-btn" disabled={loading}>
              {loading ? 'Saving...' : (project ? 'Update Project' : 'Create Project')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProjectForm;

